/**
 * ModuleAuditLog — F12.F T12.F.5.
 *
 * Historia zmian konfiguracji modułu (kto stworzył pole, edytował layout, zmienił nazwę, etc.)
 * Dane z GET /api/modules/:id/audit
 * Zakładka "Audit" w ModuleSettingsPage.
 *
 * Konwersja z raw `<table>` na `ConfigurableTable` (filtry per kolumna, server-side) —
 * mirror `KsefInvoices.tsx`/`InstanceListingTable.tsx`. Endpoint jest server-paginowany
 * (offset/limit, `PAGE_SIZE`), więc filtr per kolumna MUSI iść na backend przez
 * `?headerFilters=`, nie lokalnie po już pobranej stronie (ten sam próg co w tamtych
 * szablonach). Paginacja zostaje POZA `ConfigurableTable` (własne kontrolki niżej) —
 * ta dostaje tylko wiersze bieżącej strony, nigdy `totalItems`/`onPageChange`.
 *
 * Które kolumny mają `headerFilter` — Akcja (`action`, tekst) i Data (`created_at`, data).
 * Kolumna Użytkownik NIE ma filtra nagłówka — pokazuje skrócony UUID, nie wartość
 * przyjazną dla filtrowania. Whitelist backendu → `HEADER_FILTER_FIELDS` w
 * `apps/api/app/api/modules/[id]/audit/route.ts`.
 */

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { ClipboardList, RefreshCw, User } from 'lucide-react';

import { Button } from '@/components/Button';
import {
  type ColumnDefinition,
  type ColumnHeaderFilter,
  type ColumnHeaderFilters,
  ConfigurableTable,
} from '@/components/configurable-table';
import { Spinner } from '@/components/Spinner';
import { api } from '@/lib/api';

// ─── Types ────────────────────────────────────────────────────────────────────

interface AuditEntry {
  id: string;
  action: string;
  entityType: string | null;
  entityId: string | null;
  details: Record<string, unknown> | null;
  userId: string | null;
  createdAt: string;
}

interface AuditResponse {
  entries: AuditEntry[];
  total: number;
}

interface ModuleAuditLogProps {
  moduleId: string;
}

// ─── Action label mapping ─────────────────────────────────────────────────────

const ACTION_LABELS: Record<string, string> = {
  'custom_module.created': 'Moduł utworzony',
  'custom_module.updated': 'Moduł zaktualizowany',
  'custom_module.deleted': 'Moduł usunięty',
  'custom_module.access_updated': 'Uprawnienia zaktualizowane',
  'custom_module_field.created': 'Pole dodane',
  'custom_module_field.updated': 'Pole zaktualizowane',
  'custom_module_field.deleted': 'Pole usunięte',
  'custom_module_fields.purged': 'Pola trwale usunięte',
  'custom_module_action.created': 'Akcja dodana',
  'custom_module_action.updated': 'Akcja zaktualizowana',
  'custom_module_action.deleted': 'Akcja usunięta',
  'custom_module_category.create': 'Typ rekordu dodany',
  'custom_module_category.update': 'Typ rekordu zaktualizowany',
  'custom_module_category.delete': 'Typ rekordu usunięty',
};

function actionLabel(action: string): string {
  return ACTION_LABELS[action] ?? action;
}

function formatDate(iso: string): string {
  return new Intl.DateTimeFormat('pl-PL', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(iso));
}

// ─── Detail snippet ───────────────────────────────────────────────────────────

function DetailSnippet({ details }: { details: Record<string, unknown> | null }) {
  if (!details) return null;

  const keySnippets: string[] = [];

  if (typeof details.name === 'string') keySnippets.push(`nazwa: ${details.name}`);
  if (typeof details.slug === 'string') keySnippets.push(`slug: ${details.slug}`);
  if (typeof details.updatedRoles === 'number')
    keySnippets.push(`ról zaktualizowanych: ${details.updatedRoles}`);
  if (details.changes && typeof details.changes === 'object') {
    const keys = Object.keys(details.changes as object);
    if (keys.length > 0) keySnippets.push(`zmiany: ${keys.join(', ')}`);
  }

  if (keySnippets.length === 0) return null;

  return <span className="ml-2 text-xs text-muted-foreground">({keySnippets.join(' · ')})</span>;
}

/**
 * Serializuje `ColumnHeaderFilters` (kolumnId → filtr, stan UI) do `?headerFilters=`
 * (JSON.stringify HeaderFilter[]) — mirror `KsefInvoices.tsx`/`InstanceListingTable.tsx`.
 * KLUCZOWE: `field` w wyjściowym obiekcie MUSI być `headerFilter.fieldSlug` kolumny, NIE
 * `columnId` z UI — kontrakt z whitelistą `HEADER_FILTER_FIELDS` w
 * `apps/api/app/api/modules/[id]/audit/route.ts` operuje na backendowych slugach.
 */
export function serializeHeaderFiltersForRequest<T>(
  headerFilters: ColumnHeaderFilters,
  columns: ColumnDefinition<T>[],
): string | undefined {
  const merged: ColumnHeaderFilter[] = [];
  for (const [columnId, filter] of Object.entries(headerFilters)) {
    const col = columns.find((c) => c.id === columnId);
    const fieldSlug = col?.headerFilter?.fieldSlug ?? columnId;
    if (fieldSlug) merged.push({ ...filter, field: fieldSlug });
  }
  return merged.length > 0 ? JSON.stringify(merged) : undefined;
}

// ─── Main component ───────────────────────────────────────────────────────────

const PAGE_SIZE = 30;

export function ModuleAuditLog({ moduleId }: ModuleAuditLogProps) {
  const { t } = useTranslation();
  const [offset, setOffset] = useState(0);
  // Filtry per kolumna z popupu w nagłówku (Kreator Listy) — server-side, patrz
  // `serializedHeaderFilters` niżej.
  const [headerFilters, setHeaderFilters] = useState<ColumnHeaderFilters>({});

  const columns = useMemo<ColumnDefinition<AuditEntry>[]>(
    () => [
      {
        id: 'action',
        accessor: 'action',
        header: t('tenant.moduleSettings.audit.action'),
        sortable: false,
        headerFilter: { category: 'text', fieldSlug: 'action' },
        renderCell: (_value, row) => (
          <>
            <span className="font-medium">{actionLabel(row.action)}</span>
            <DetailSnippet details={row.details} />
          </>
        ),
      },
      {
        id: 'user',
        accessor: 'userId',
        header: t('tenant.moduleSettings.audit.user'),
        sortable: false,
        renderCell: (_value, row) =>
          row.userId ? (
            <span className="inline-flex items-center gap-1 text-muted-foreground">
              <User className="h-3.5 w-3.5" />
              <span className="font-mono text-xs">{row.userId.slice(0, 8)}…</span>
            </span>
          ) : (
            <span className="text-muted-foreground/60">—</span>
          ),
      },
      {
        id: 'date',
        accessor: 'createdAt',
        header: t('tenant.moduleSettings.audit.date'),
        sortable: false,
        headerFilter: { category: 'date', fieldSlug: 'created_at' },
        renderCell: (_value, row) => (
          <span className="text-muted-foreground">{formatDate(row.createdAt)}</span>
        ),
      },
    ],
    [t],
  );

  const serializedHeaderFilters = useMemo(
    () => serializeHeaderFiltersForRequest(headerFilters, columns),
    [headerFilters, columns],
  );

  // Reset paginacji przy zmianie filtra — inaczej user mógłby wylądować na pustej
  // stronie po zawężeniu wyniku (mirror `ActiveInstancesTab.tsx`).
  function handleHeaderFiltersChange(next: ColumnHeaderFilters) {
    setHeaderFilters(next);
    setOffset(0);
  }

  const query = useQuery<AuditResponse>({
    queryKey: ['module-audit', moduleId, offset, serializedHeaderFilters],
    queryFn: () => {
      const params = new URLSearchParams();
      params.set('limit', String(PAGE_SIZE));
      params.set('offset', String(offset));
      if (serializedHeaderFilters) params.set('headerFilters', serializedHeaderFilters);
      return api
        .get<AuditResponse>(`/modules/${moduleId}/audit?${params.toString()}`)
        .then((r) => r.data);
    },
    enabled: !!moduleId,
  });

  const entries = query.data?.entries ?? [];
  const total = query.data?.total ?? 0;
  const hasMore = offset + PAGE_SIZE < total;
  const hasPrev = offset > 0;
  // Zero wierszy z aktywnym filtrem per kolumna NIE jest "brak danych" — to "brak
  // wyników filtra". Manualny empty-state (bez tabeli) byłby dead-endem: chowa CAŁĄ
  // tabelę WŁĄCZNIE z popupem filtra w nagłówku, więc user nie miałby czym wyczyścić
  // filtra. Gdy filtr jest aktywny, przepuszczamy dalej do `ConfigurableTable` — ta
  // renderuje WŁASNY empty-state ("Brak wyników" + przycisk "Wyczyść filtry") z
  // zachowanym wierszem filtrów (mirror `ActiveInstancesTab.tsx`).
  const hasActiveHeaderFilter = Object.keys(headerFilters).length > 0;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          {t('tenant.moduleSettings.audit.description')}
        </p>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => void query.refetch()}
          disabled={query.isFetching}
          aria-label={t('common.refresh')}
        >
          <RefreshCw className={`h-4 w-4 ${query.isFetching ? 'animate-spin' : ''}`} />
        </Button>
      </div>

      {query.isLoading && (
        <div className="flex items-center justify-center py-12">
          <Spinner />
        </div>
      )}

      {query.isError && (
        <div className="rounded-md border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
          {t('common.loadError')}
        </div>
      )}

      {!query.isLoading && !query.isError && entries.length === 0 && !hasActiveHeaderFilter && (
        <div className="flex flex-col items-center justify-center gap-2 py-12 text-center text-sm text-muted-foreground">
          <ClipboardList className="h-8 w-8 opacity-30" />
          <p>{t('tenant.moduleSettings.audit.empty')}</p>
        </div>
      )}

      {!query.isLoading && !query.isError && (entries.length > 0 || hasActiveHeaderFilter) && (
        <>
          {/* Tabela — Kreator Listy (ConfigurableTable): filtry per kolumna server-side przez
              `headerFilters` (kontrolowane, patrz `serializedHeaderFilters` wyżej — endpoint jest
              server-paginowany, filtr MUSI iść na backend, nie po pobranej stronie). Wiersz filtrów
              renderuje kontrolki typowane (menu operatorów + edytor) dla kolumn z `headerFilter`;
              kolumny bez niego (użytkownik) NIE dostają pola — ekran nie kontroluje `columnFilters`,
              więc `FilterRowCell` daje pusty placeholder zamiast lokalnego filtra pobranej strony.
              `persistConfig={false}` — brak zapisu layoutu (mirror `KsefInvoices.tsx`). Paginacja
              zostaje POZA tabelą (kontrolki niżej) — `ConfigurableTable` dostaje TYLKO wiersze
              bieżącej strony, nigdy `totalItems`/`onPageChange`. */}
          <ConfigurableTable<AuditEntry>
            tableId="module_audit_log"
            testIdPrefix="module-audit-log"
            columns={columns}
            data={entries}
            getRowId={(row) => row.id}
            headerFilters={headerFilters}
            onHeaderFiltersChange={handleHeaderFiltersChange}
            persistConfig={false}
            showViewModeToggle={false}
          />

          {/* Pagination — poza ConfigurableTable, własne kontrolki (offset/limit). */}
          {total > 0 && (
            <div className="flex items-center justify-between text-sm text-muted-foreground">
              <span>
                {t('tenant.moduleSettings.audit.showing', {
                  from: offset + 1,
                  to: Math.min(offset + PAGE_SIZE, total),
                  total,
                })}
              </span>
              <div className="flex gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
                  disabled={!hasPrev || query.isFetching}
                >
                  {t('common.prev')}
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setOffset((o) => o + PAGE_SIZE)}
                  disabled={!hasMore || query.isFetching}
                >
                  {t('common.next')}
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
